import { useCallback, useEffect, useState } from "react";
import { Plus, Package, ClipboardList, Truck, Factory } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import { getFloorRequests } from "../../../store/slices/Factory/newFloorSlice";
import {
  cancelThirdPartyRequest,
  completeThirdPartyInward,
  getThirdPartyInwardBatches,
  getThirdPartyRequests,
  processThirdPartyInward,
} from "../../../store/slices/Factory/newStoreSlice"; // Slice where getThirdPartyRequests resides
import { IFloorRequest } from "../../../models/floor.model";
import NewFloorRequestsPage from "../Floor/NewFloorRequestsPage";
import FloorRequestDetailsModal from "../Floor/FloorItemsRequestModal";
import AddStoreInwardModal from "./AddStoreInwardModal";
import NewStoreStock from "./NewStoreStock";
import ThirdPartyOutwardModal from "./ThirdPartyOutwardModal";
import ThirdPartyRequestsPage from "./ThirdPartyRequestsPage";
import { IThirdPartyRequest } from "../../../models/thirdPartyRequest.model";
import ThirdPartyInwardToStoreModal from "./ThirdPartyInwardModal";
import ThirdPartyBatchesModal from "./ThirdPartyBatchesModal";
import { ICreateThirdPartyInwardPayload } from "../../../api/Factory/store.api";

export default function StoreInventoryPage() {
  const dispatch: RootDispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state: "request" | "stock" | "thirdparty"
  const [mainTab, setMainTab] = useState<"request" | "stock" | "thirdparty">(
    () => {
      const sub = searchParams.get("subtab");
      if (sub === "stock" || sub === "thirdparty") return sub;
      return "request";
    },
  );

  const [isInwardModalOpen, setIsInwardModalOpen] = useState(false);
  const [isThirdPartyModalOpen, setIsThirdPartyModalOpen] = useState(false);

  // Floor Requests Filters
  const [requestTab, setRequestTab] = useState<
    "pending" | "approved" | "rejected" | "cancelled"
  >(() => {
    const status = searchParams.get("status");
    if (
      status === "pending" ||
      status === "approved" ||
      status === "rejected"
    ) {
      return status;
    }
    return "pending";
  });

  // Third Party Filters
  const [thirdPartyStatus, setThirdPartyStatus] = useState<string>(
    searchParams.get("tpStatus") || "dispatched",
  );
  const [thirdPartyVendor, setThirdPartyVendor] = useState<string>(
    searchParams.get("vendorId") || "",
  );
  const [thirdPartyPage, setThirdPartyPage] = useState<number>(1);

  const [stockTab] = useState<"batches" | "all">(() => {
    const view = searchParams.get("view");
    return view === "all" ? "all" : "batches";
  });
  const [selectedRequest, setSelectedRequest] = useState<IFloorRequest>();
  const [floorRequestpage, setFloorRequestPage] = useState(1);

  // Redux Selectors
  const {
    requests,
    loading: floorLoading,
    pagination: floorPagination,
  } = useSelector((state: RootState) => state.newFloor);

  const {
    thirdPartyRequests,
    loading: storeLoading,
    pagination: storePagination,
    thirdPartyInwardBatches,
  } = useSelector((state: RootState) => state.newStore);

  const updateParams = (
    tab: "request" | "stock" | "thirdparty",
    options?: { status?: string; vendorId?: string; view?: string },
  ) => {
    const params = new URLSearchParams(searchParams);
    params.set("subtab", tab);

    if (tab === "request") {
      params.set("status", options?.status ?? requestTab);
      params.delete("view");
      params.delete("tpStatus");
      params.delete("vendorId");
    } else if (tab === "thirdparty") {
      params.set("tpStatus", options?.status ?? thirdPartyStatus);
      if (options?.vendorId || thirdPartyVendor) {
        params.set("vendorId", options?.vendorId ?? thirdPartyVendor);
      } else {
        params.delete("vendorId");
      }
      params.delete("status");
      params.delete("view");
    } else {
      params.set("view", options?.view ?? stockTab);
      params.delete("status");
      params.delete("tpStatus");
      params.delete("vendorId");
    }
    setSearchParams(params);
  };

  // Callback to fetch floor requests
  const getRequestsCallback = useCallback(async () => {
    dispatch(
      getFloorRequests({
        params: {
          page: floorRequestpage,
          limit: 12,
          status: requestTab,
        },
      }),
    );
  }, [dispatch, floorRequestpage, requestTab]);

  // Callback to fetch Third Party requests
  const getThirdPartyRequestsCallback = useCallback(async () => {
    dispatch(
      getThirdPartyRequests({
        params: {
          page: thirdPartyPage,
          limit: 10,
          status: thirdPartyStatus === "all" ? undefined : thirdPartyStatus,
          vendorId: thirdPartyVendor || undefined,
        },
      }),
    );
  }, [dispatch, thirdPartyPage, thirdPartyStatus, thirdPartyVendor]);

  useEffect(() => {
    if (mainTab === "request") {
      getRequestsCallback();
    } else if (mainTab === "thirdparty") {
      getThirdPartyRequestsCallback();
    }
  }, [mainTab, getRequestsCallback, getThirdPartyRequestsCallback]);

  const handleRequestTabChange = (
    status: "pending" | "approved" | "rejected" | "cancelled",
  ) => {
    setRequestTab(status);
    setFloorRequestPage(1);
    updateParams("request", { status });
  };

  const handleThirdPartyStatusChange = (status: string) => {
    setThirdPartyStatus(status);
    setThirdPartyPage(1);
    updateParams("thirdparty", { status });
  };

  const handleThirdPartyVendorChange = (vendorId: string) => {
    setThirdPartyVendor(vendorId);
    setThirdPartyPage(1);
    updateParams("thirdparty", { vendorId });
  };
  //inward modal
  const submittingInward = useSelector(
    (state: RootState) => state.newStore.loading.thirdPartyInwardLoading,
  );
  const [selectedThirdPartyRequest, setSelectedThirdPartyRequest] =
    useState<IThirdPartyRequest | null>(null);
  const [isThirPartyInwardModalOpen, setIsThirPartyInwardModalOpen] =
    useState(false);
  const handleOpenInwardModal = (req: IThirdPartyRequest) => {
    setSelectedThirdPartyRequest(req);
    setIsThirPartyInwardModalOpen(true);
  };

  const handleThirdpartyCloseInwardModal = () => {
    setSelectedThirdPartyRequest(null);
    setIsThirPartyInwardModalOpen(false);
  };

  const handleThridPartyInwardSubmit = async (
    payload: ICreateThirdPartyInwardPayload,
  ) => {
    const res = await dispatch(processThirdPartyInward(payload)).unwrap();
    if (res.success) {
      handleThirdpartyCloseInwardModal();
      getThirdPartyRequestsCallback();
    }
  };
  const handleCancleThirdPartyRequest = async (request: any) => {
    const res = await dispatch(
      cancelThirdPartyRequest({ id: request._id }),
    ).unwrap();
    if (res.success) {
      handleThirdpartyCloseInwardModal();
      getThirdPartyRequestsCallback();
    }
  };

  const handleCompleteThirdPartyInward = async (
    request: IThirdPartyRequest,
    costs: {
      productionCost?: number;
      receivingTransportCost?: number;
      receivingExtraCost?: number;
    },
  ) => {
    const res = await dispatch(
      completeThirdPartyInward({
        thirdPartyRequestId: request._id,
        ...costs,
      }),
    ).unwrap();
    if (res.success) {
      getThirdPartyRequestsCallback();
    }
  };

  // View batches modal
  const loadingThirdPartyInwardBatches = useSelector(
    (state: RootState) =>
      state.newStore.loading.getThirdPartyInwardBatchesLoading,
  );
  const [isBatchesModalOpen, setIsBatchesModalOpen] = useState(false);
  const handleViewBatches = (request: IThirdPartyRequest) => {
    setIsBatchesModalOpen(true);
    dispatch(
      getThirdPartyInwardBatches({ params: { requestId: request._id } }),
    );
  };
  const handleCloseBatchesModal = () => {
    setIsBatchesModalOpen(false);
  };
  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      {/* Header Panel */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
            Stock Inventory
          </h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">
            Manage inward requests, third-party dispatches, and store stock.
          </p>
        </div>

        {/* Action Buttons Group */}
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
          {/* Third Party Action Button */}
          <button
            className="flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-3 text-white font-medium shadow-xs hover:bg-amber-700 active:bg-amber-800 transition text-sm md:text-base w-full sm:w-auto"
            onClick={() => setIsThirdPartyModalOpen(true)}
          >
            <Truck size={18} />
            Third Party Outward
          </button>

          {/* Inward Material Button */}
          <button
            className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-white font-medium shadow-xs hover:bg-indigo-700 active:bg-indigo-800 transition text-sm md:text-base w-full sm:w-auto"
            onClick={() => setIsInwardModalOpen(true)}
          >
            <Plus size={18} />
            Inward Material
          </button>
        </div>
      </div>

      {/* Main Container Layout Card Wrapper */}
      <div className="rounded-2xl bg-white shadow-xs border border-slate-200 overflow-hidden">
        {/* Main Header Tabs */}
        <div className="border-b flex w-full overflow-x-auto scrollbar-none">
          <MainTab
            active={mainTab === "request"}
            icon={<ClipboardList size={18} />}
            title="Request"
            onClick={() => {
              setMainTab("request");
              updateParams("request");
            }}
          />

          <MainTab
            active={mainTab === "thirdparty"}
            icon={<Factory size={18} />}
            title="Third Party"
            onClick={() => {
              setMainTab("thirdparty");
              updateParams("thirdparty");
            }}
          />

          <MainTab
            active={mainTab === "stock"}
            icon={<Package size={18} />}
            title="Store Stock"
            onClick={() => {
              setMainTab("stock");
              updateParams("stock");
            }}
          />
        </div>

        {/* Tab Router Content Output */}
        {mainTab === "request" && (
          <NewFloorRequestsPage
            requests={requests}
            loading={floorLoading.getFloorRequestsLoading}
            pagination={floorPagination}
            requestTab={requestTab as any}
            floorRequestpage={floorRequestpage}
            setFloorRequestPage={setFloorRequestPage}
            onRequestTabChange={handleRequestTabChange}
            onSelectRequest={setSelectedRequest}
          />
        )}

        {mainTab === "thirdparty" && (
          <ThirdPartyRequestsPage
            requests={thirdPartyRequests}
            loading={storeLoading.getThirdpartyrequestsLoading}
            pagination={storePagination?.getThirdPartyRequests}
            statusTab={thirdPartyStatus}
            vendorFilter={thirdPartyVendor}
            page={thirdPartyPage}
            onStatusTabChange={handleThirdPartyStatusChange}
            onVendorFilterChange={handleThirdPartyVendorChange}
            onPageChange={setThirdPartyPage}
            onInwardToStore={handleOpenInwardModal}
            onCancelRequest={handleCancleThirdPartyRequest}
            onCompleteInward={handleCompleteThirdPartyInward}
            onViewBatches={handleViewBatches}
          />
        )}

        {mainTab === "stock" && <NewStoreStock />}
      </div>

      <AddStoreInwardModal
        isOpen={isInwardModalOpen}
        onClose={() => setIsInwardModalOpen(false)}
      />
      <ThirdPartyOutwardModal
        isOpen={isThirdPartyModalOpen}
        onClose={() => {
          setIsThirdPartyModalOpen(false);
          if (mainTab === "thirdparty") getThirdPartyRequestsCallback();
        }}
      />
      <ThirdPartyInwardToStoreModal
        isOpen={isThirPartyInwardModalOpen}
        request={selectedThirdPartyRequest}
        onClose={handleThirdpartyCloseInwardModal}
        onSubmit={handleThridPartyInwardSubmit}
        isLoading={submittingInward}
      />
      <ThirdPartyBatchesModal
        isOpen={isBatchesModalOpen}
        data={thirdPartyInwardBatches}
        loading={loadingThirdPartyInwardBatches}
        onClose={handleCloseBatchesModal}
      />
      {selectedRequest && (
        <FloorRequestDetailsModal
          onClose={async (success) => {
            setSelectedRequest(undefined);
            if (success) {
              await getRequestsCallback();
            }
          }}
          request={selectedRequest}
        />
      )}
    </div>
  );
}

function MainTab({
  active,
  title,
  icon,
  onClick,
}: {
  active: boolean;
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 md:px-6 py-4 font-medium transition border-b-2 text-sm md:text-base whitespace-nowrap ${
        active
          ? "border-indigo-600 text-indigo-600 bg-indigo-50/50"
          : "border-transparent text-slate-500 hover:bg-slate-50"
      }`}
    >
      {icon}
      <span>{title}</span>
    </button>
  );
}
